import { isEqual, mapValues, pick, uniq } from 'es-toolkit';
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { castDraft, Draft, original } from 'immer';

import { assertUnlessInProduction } from '@paragon/client-tools';
import {
  AbsoluteVerticalCut,
  AbsoluteVerticalPlane,
  BearingEnvelope,
  BearingEnvelopeType,
  CeilingPlane,
  CeilingTray,
  CreateTrussError,
  DEFAULT_BEVEL_CUT_TYPE_PREFERENCE,
  FeaturePatternTrussContainer,
  getDefinitionTypeForComponentType,
  LayoutFeatureArea,
  LayoutLoad,
  LayoutUserDistanceDimension,
  ProjectLayout,
  RoofContainer,
  RoofPlane,
  SnappingUnderlay,
  TrussEnvelope,
  WorkLine,
  WorkLineScope,
} from '@paragon/contract-library';
import { slopeToDegrees } from '@paragon/geometry-library';
import {
  DEFAULT_HEEL_SETTINGS,
  NonGeometricInsertionCeilingPlane,
  NonGeometricInsertionRoofPlane,
} from '@paragon/layout';
import { assertExhaustiveSwitch, notNullish, notUndefined, pickBy, ReadonlyRecord } from '@paragon/utilities';

import { CardinalDirection } from '../../library/layout-geometry/directions';
import { INITIAL_TRUSS_ENVELOPE_PROPERTIES } from '../../library/layout-geometry/truss-envelopes/defaults';
import {
  TRUSS_ENVELOPE_PROPERTIES_PERSISTED_IN_SETTINGS,
  TrussEnvelopeInsertionSettings,
  TrussEnvelopeSnappingBehaviorMode,
  TrussEnvelopeSnappingSettings,
} from '../../library/layout-geometry/truss-envelopes/types';
import {
  CORE_ENTITY_SNAP_TARGET_TYPES,
  EntitySnapTargetType,
  SerializedEntitySnapTarget,
} from '../../library/layout-snapping/EntitySnapTarget';
import { InteractiveEditSnappingSettings } from '../../library/layout-snapping/interactive-snapping/settings';
import { PointSnappingSettings } from '../../library/layout-snapping/point-snapping/settings';
import { EntitySnapTargetSelectionSettings } from '../../library/layout-snapping/settings';
import { sceneHoverCleared } from '../actions/sceneHoverActions';
import {
  applicationOnInsertingBearingEnvelope,
  applicationOnInsertingTrussEnvelope,
  applicationOnPickingLayoutPoint,
  InsertionFeaturePatternContainer,
  NonInsertionTrussEnvelopeTargetInfo,
  resetInsertionTrussEnvelope,
  setDimensionMode,
  setInsertionTrussEnvelopeProperties,
  updateInsertingBearingEnvelope,
  updateInsertingCeilingPlane,
  updateInsertingRoofPlane,
  updateInsertionFeaturePatternContainer,
} from './applicationSlice';
import { interactiveEditingSlice } from './interactiveEditingSlice';
import { applyUserSettings } from './userSettingsSlice';

const undoRedoKeys = [
  'bearingEnvelopes',
  'ceilingPlanes',
  'ceilingTrays',
  'featurePatternContainers',
  'layoutLoads',
  'recentRoofPlaneInsertions',
  'roofContainers',
  'roofPlanes',
  'snappingUnderlays',
  'trussEnvelopes',
  'workLines',
  'trussEnvelopeErrors',
] as const satisfies (keyof LayoutState)[];

type UndoRedoKey = (typeof undoRedoKeys)[number];
export type LayoutUndoRedoState = Pick<LayoutState, UndoRedoKey>;

/**
 * Used to track what levels are affected by a given undo/redo operation. A `null` value is allowed and indicates that
 *   the operation affected a "ghost object" that does not belong to a specific level.
 */
type AffectedLevelGuids = readonly (string | null)[];

/** `null` when the edited work lines need no particular level to be visible */
type WorkLineAffectedLevelGuids = readonly string[] | null;

interface UndoSnapshot {
  readonly state: LayoutUndoRedoState;
  readonly previous: UndoSnapshot | null;
  readonly affectedLevels: AffectedLevelGuids;
  readonly insertedBearingEnvelopeGuid?: string;
}

interface RedoSnapshot {
  readonly state: LayoutUndoRedoState;
  readonly next: RedoSnapshot | null;
  readonly affectedLevels: AffectedLevelGuids;
  readonly insertedBearingEnvelopeGuid?: string;
  readonly restoresBearingEnvelopeInsertionChain?: boolean;
}

interface UndoRedoChain {
  readonly undo: UndoSnapshot | null;
  readonly redo: RedoSnapshot | null;

  readonly affectedLevelsForCurrentChanges: AffectedLevelGuids;
}

const BASE_UNDO_REDO_CHAIN: UndoRedoChain = Object.freeze({
  undo: null,
  redo: null,
  affectedLevelsForCurrentChanges: Object.freeze([]),
});

const BEAM_PROPERTIES_PERSISTED_IN_SETTINGS = [
  'thickness',
  'numberOfPlies',
  'beamOrientation',
  'justification',
  'top',
  'bottom',
  'levelGuid',
] as const satisfies (keyof BearingEnvelope)[];

const WALL_PROPERTIES_PERSISTED_IN_SETTINGS = [
  'thickness',
  'nonStructural',
  'justification',
  'top',
  'bottom',
  'levelGuid',
] as const satisfies (keyof BearingEnvelope)[];

type BeamInsertionDefaultProperties = Pick<BearingEnvelope, (typeof BEAM_PROPERTIES_PERSISTED_IN_SETTINGS)[number]>;
type WallInsertionDefaultProperties = Pick<BearingEnvelope, (typeof WALL_PROPERTIES_PERSISTED_IN_SETTINGS)[number]>;

export interface BearingEnvelopeInsertionSettings<
  TDefaultProperties extends BeamInsertionDefaultProperties | WallInsertionDefaultProperties =
    BeamInsertionDefaultProperties | WallInsertionDefaultProperties,
> {
  insertRoofPlane: boolean;

  // if true (the default), new bearing envelopes will receive default elevations from the active level
  setElevationsFromLevel: boolean;

  // if true, the bearing envelope is inserted as soon as both endpoints have been picked, without waiting
  // for the user to click Insert
  autoInsertOnSecondPoint: boolean;

  defaultProperties: TDefaultProperties | null;

  // When set, bearing envelope point picking keeps the moving endpoint this distance away from the fixed endpoint.
  lockedLength: number | null;

  // When set, the user has chosen a direction for the bearing envelope before its second endpoint is known.
  lockedDirection: CardinalDirection | null;
}

const DEFAULT_BEARING_ENVELOPE_INSERTION_SETTINGS = {
  insertRoofPlane: false,
  setElevationsFromLevel: true,
  autoInsertOnSecondPoint: false,
  defaultProperties: null,
  lockedLength: null,
  lockedDirection: null,
} satisfies BearingEnvelopeInsertionSettings<BeamInsertionDefaultProperties | WallInsertionDefaultProperties>;

export function getBearingEnvelopeInsertionSettings(
  state: Pick<LayoutState, 'bearingEnvelopeInsertionSettings'>,
  bearingType: BearingEnvelopeType,
): BearingEnvelopeInsertionSettings {
  switch (bearingType) {
    case 'Beam':
      return state.bearingEnvelopeInsertionSettings.beam;

    case 'Wall':
      return state.bearingEnvelopeInsertionSettings.wall;

    default:
      assertExhaustiveSwitch(bearingType);
  }
}

export interface LayoutState {
  readonly roofContainers: ReadonlyRecord<string, RoofContainer>;
  readonly featurePatternContainers: Record<string, FeaturePatternTrussContainer>;
  readonly trussEnvelopes: ReadonlyRecord<string, TrussEnvelope>;
  readonly bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>;

  readonly workLines: ReadonlyRecord<string, WorkLine>;
  readonly userDistanceDimensions: LayoutUserDistanceDimension[];
  readonly localUserDistanceDimensions: LayoutUserDistanceDimension[];

  readonly roofPlanes: ReadonlyRecord<string, RoofPlane>;
  readonly ceilingPlanes: ReadonlyRecord<string, CeilingPlane>;
  readonly ceilingTrays: ReadonlyRecord<string, CeilingTray>;

  readonly layoutLoads: ReadonlyRecord<string, LayoutLoad>;

  readonly snappingUnderlays: ReadonlyRecord<string, SnappingUnderlay>;

  /**
   * Truss envelope errors reported by the server are stored here. Client side errors and which error to display are
   * computed by selectors.
   */
  readonly trussEnvelopeErrors: ReadonlyRecord<string, CreateTrussError>;

  /**
   * These represent the user's configured snapping settings. They can be altered (narrowed) by other factors, such as
   *   layout render modes (ex: we don't want to snap to hidden objects). Prefer using `getActualPointSnappingSettings`
   *   or `getActualPointSnappingContextAndSettings` over accessing these configured settings directly.
   */
  readonly pointSnappingSettings: {
    readonly general: PointSnappingSettings;
    readonly bearingEnvelope: PointSnappingSettings;
    readonly trussEnvelope: PointSnappingSettings;
  };

  readonly interactiveEditSnappingSettings: InteractiveEditSnappingSettings;

  readonly trussEnvelopeInsertionSettings: TrussEnvelopeInsertionSettings;

  readonly bearingEnvelopeInsertionSettings: {
    readonly beam: BearingEnvelopeInsertionSettings<BeamInsertionDefaultProperties>;
    readonly wall: BearingEnvelopeInsertionSettings<WallInsertionDefaultProperties>;
  };

  /**
   * The guids of all roof planes inserted during the current ongoing usage of the insertion panel, or meaningless
   * otherwise.
   */
  readonly recentRoofPlaneInsertions: readonly string[];

  readonly roofPlaneInsertionSettings: {
    readonly autoCutOnInsert: boolean;
    defaultElevationIfAbsolute: number;
    defaultProperties: Pick<
      RoofPlane,
      | 'flipped'
      | 'slopeInDegrees'
      | 'heelSettings'
      | 'deckingThickness'
      | 'topChordWidth'
      | 'overhang'
      | 'subfasciaThickness'
      | 'cantilever'
      | 'levelGuid'
    >;
  };

  readonly ceilingPlaneInsertionSettings: {
    defaultElevationIfAbsolute: number;
    defaultProperties: Pick<CeilingPlane, 'flipped' | 'slopeInDegrees'>;
  };

  readonly featurePatternContainerInsertionSettings: {
    defaultElevations: { topElevation: number; bottomElevation: number } | null;
    defaultSlopeSettings: InsertionFeaturePatternContainer['slopeSettings'];
  };

  readonly hoveredCutFacet: {
    readonly targetInfo: NonInsertionTrussEnvelopeTargetInfo;
    readonly topOrBottom: 'top' | 'bottom';
    readonly cutIndex: number;
  } | null;

  // A plane that should be visually highlighted in the scene, barring any other conflicting state that would
  //   cause highlighting. Set this whenever you want to draw the user's attention (strongly) to a specific plane.
  readonly highlightedPlane:
    | { readonly type: 'ceilingPlane'; readonly guid: string }
    | { readonly type: 'roofPlane'; readonly guid: string }
    | ({ readonly type: 'absoluteVertical' } & AbsoluteVerticalPlane)
    | null;

  // Because roof face visuals are complicated, we need to track hover status at a high level. This property is used to
  //   determine which face(s) should receive "selectability" hover effects, and is distinct in purpose and treatment
  //   from the highlighting properties above, `hoveredPlanes` in the application slice, and from `viewport.hoveredItem`
  //   which controls tooltips.
  readonly hoveredRoofFace:
    | { readonly type: 'roofOrCeilingPlane'; readonly guid: string }
    | { readonly type: 'ceilingTrayPolygon'; readonly guid: string }
    | null;

  readonly hoveredWorkLineGuids: readonly string[];

  readonly hoveredBearingEnvelopeEdge: {
    readonly guid: string;
    readonly edgeAngle: number;
  } | null;

  readonly hoveredBearingEnvelopeInterior: {
    readonly guid: string;
  } | null;

  readonly undoRedoChain: UndoRedoChain;
}

export type PointSnappingContext = keyof LayoutState['pointSnappingSettings'];
export type SnappingSettingsContext = PointSnappingContext | 'interactiveEdit';

const DEFAULT_TRUSS_ENVELOPE_SNAPPING_SETTINGS: TrussEnvelopeSnappingSettings = {
  distanceSettings: 'automatic',
  lockedTarget: null,
  behaviorMode: 'closest-result',
};

function createDefaultPointSnappingSettings(): PointSnappingSettings {
  return {
    enabledTargetTypes: [...CORE_ENTITY_SNAP_TARGET_TYPES],

    entitySnaps: {
      intersections: true,
      extensionIntersections: false,
      midpoints: true,
      endpoints: true,
      nearestPoint: false,
      perpendicularProjection: true,
    },

    segmentSnaps: {
      orthogonal: false,
      angle: false,
      lengthIncrement: false,
    },
  };
}

const DEFAULT_POINT_SNAPPING_SETTINGS = createDefaultPointSnappingSettings();

const DEFAULT_BEARING_ENVELOPE_POINT_SNAPPING_SETTINGS: PointSnappingSettings = {
  ...createDefaultPointSnappingSettings(),
  segmentSnaps: { lengthIncrement: true, orthogonal: true, angle: true },
};

const DEFAULT_TRUSS_ENVELOPE_POINT_SNAPPING_SETTINGS = createDefaultPointSnappingSettings();

const DEFAULT_INTERACTIVE_EDIT_SNAPPING_SETTINGS: InteractiveEditSnappingSettings = {
  enabledTargetTypes: [...CORE_ENTITY_SNAP_TARGET_TYPES],
  footGrid: true,
  entityAxisSnaps: {
    endpoints: true,
    midpoints: true,
    axisAlignedLines: true,
  },
};

export const INITIAL_ROOF_PLANE_PROPERTIES = {
  flipped: false,
  slopeInDegrees: slopeToDegrees(4),
  heelSettings: DEFAULT_HEEL_SETTINGS,
  deckingThickness: 0,
  topChordWidth: 3.5,
  overhang: 24,
  subfasciaThickness: 0,
  cantilever: 0,
  cuts: [],
  levelGuid: null,
} satisfies Omit<NonGeometricInsertionRoofPlane, 'guid'>;

const ROOF_PLANE_PROPERTIES_PERSISTED_IN_SETTINGS = [
  'flipped',
  'slopeInDegrees',
  'heelSettings',
  'deckingThickness',
  'topChordWidth',
  'overhang',
  'subfasciaThickness',
  'cantilever',
  'levelGuid',
] as const satisfies (keyof LayoutState['roofPlaneInsertionSettings']['defaultProperties'])[];

export const INITIAL_CEILING_PLANE_PROPERTIES = {
  flipped: false,
  slopeInDegrees: 0,
  cuts: [],
  levelGuid: null,
} satisfies Omit<NonGeometricInsertionCeilingPlane, 'guid'>;

const CEILING_PLANE_PROPERTIES_PERSISTED_IN_SETTINGS = [
  'flipped',
  'slopeInDegrees',
] as const satisfies (keyof LayoutState['ceilingPlaneInsertionSettings']['defaultProperties'])[];

const initialState: LayoutState = {
  roofContainers: {},
  featurePatternContainers: {},
  trussEnvelopes: {},
  bearingEnvelopes: {},

  workLines: {},
  userDistanceDimensions: [],
  localUserDistanceDimensions: [],

  roofPlanes: {},
  ceilingPlanes: {},
  ceilingTrays: {},

  layoutLoads: {},

  snappingUnderlays: {},

  trussEnvelopeErrors: {},

  trussEnvelopeInsertionSettings: {
    snapping: DEFAULT_TRUSS_ENVELOPE_SNAPPING_SETTINGS,
    bevelCutTypePreference: DEFAULT_BEVEL_CUT_TYPE_PREFERENCE,
    lastComponentType: 'RoofTruss',
    lastRoofTrussDefinitionType: 'Profile',
    defaultProperties: INITIAL_TRUSS_ENVELOPE_PROPERTIES,
  },

  pointSnappingSettings: {
    general: DEFAULT_POINT_SNAPPING_SETTINGS,
    bearingEnvelope: DEFAULT_BEARING_ENVELOPE_POINT_SNAPPING_SETTINGS,
    trussEnvelope: DEFAULT_TRUSS_ENVELOPE_POINT_SNAPPING_SETTINGS,
  },

  interactiveEditSnappingSettings: DEFAULT_INTERACTIVE_EDIT_SNAPPING_SETTINGS,

  bearingEnvelopeInsertionSettings: {
    beam: { ...DEFAULT_BEARING_ENVELOPE_INSERTION_SETTINGS },
    wall: { ...DEFAULT_BEARING_ENVELOPE_INSERTION_SETTINGS },
  },

  recentRoofPlaneInsertions: [],

  roofPlaneInsertionSettings: {
    autoCutOnInsert: false,
    defaultProperties: pick(INITIAL_ROOF_PLANE_PROPERTIES, ROOF_PLANE_PROPERTIES_PERSISTED_IN_SETTINGS),
    defaultElevationIfAbsolute: 0,
  },

  ceilingPlaneInsertionSettings: {
    defaultProperties: pick(INITIAL_CEILING_PLANE_PROPERTIES, CEILING_PLANE_PROPERTIES_PERSISTED_IN_SETTINGS),
    defaultElevationIfAbsolute: 0,
  },

  featurePatternContainerInsertionSettings: {
    defaultElevations: null,
    defaultSlopeSettings: null,
  },

  hoveredCutFacet: null,

  highlightedPlane: null,

  hoveredRoofFace: null,

  hoveredWorkLineGuids: [],

  hoveredBearingEnvelopeEdge: null,

  hoveredBearingEnvelopeInterior: null,

  undoRedoChain: BASE_UNDO_REDO_CHAIN,
};

export function areTrussEnvelopeSnappingSettingsModified(settings: TrussEnvelopeSnappingSettings): boolean {
  return !isEqual(settings, DEFAULT_TRUSS_ENVELOPE_SNAPPING_SETTINGS);
}

export function arePointSnappingSettingsModified(
  context: PointSnappingContext,
  settings: PointSnappingSettings,
): boolean {
  return !isEqual(settings, initialState.pointSnappingSettings[context]);
}

export function areInteractiveEditSnappingSettingsModified(settings: InteractiveEditSnappingSettings): boolean {
  return !isEqual(settings, initialState.interactiveEditSnappingSettings);
}

function toggleEnabledEntitySnapTarget(
  settings: Draft<EntitySnapTargetSelectionSettings>,
  target: EntitySnapTargetType,
) {
  const enabled = new Set(settings.enabledTargetTypes);
  settings.enabledTargetTypes = CORE_ENTITY_SNAP_TARGET_TYPES.filter((type) =>
    type === target ? !enabled.has(target) : enabled.has(type),
  );
}

export const layoutSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    layoutDataLoaded: (state, action: PayloadAction<ProjectLayout>) => {
      state.bearingEnvelopes = action.payload.bearingEnvelopes;
      state.ceilingPlanes = castDraft(action.payload.ceilingPlanes);
      state.ceilingTrays = castDraft(action.payload.ceilingTrays);
      state.featurePatternContainers = castDraft(action.payload.featurePatternContainers);
      state.roofContainers = castDraft(action.payload.roofContainers);
      state.roofPlanes = castDraft(action.payload.roofPlanes);
      state.trussEnvelopes = castDraft(action.payload.trussEnvelopes);
      state.layoutLoads = castDraft(action.payload.layoutLoads);
      state.workLines = castDraft(action.payload.workLines);
      state.userDistanceDimensions = castDraft(action.payload.userDistanceDimensions ?? []);
      state.snappingUnderlays = castDraft(action.payload.snappingUnderlays ?? {});
    },

    roofContainerUpserted: (state, action: PayloadAction<RoofContainer>) => {
      const existingContainer = state.roofContainers[action.payload.guid];
      addOldStateToUndoChain(original(state)!, state, [existingContainer?.levelGuid, action.payload.levelGuid]);

      state.roofContainers[action.payload.guid] = castDraft(action.payload);
    },

    roofContainerDeleted: (state, action: PayloadAction<string>) => {
      const container = state.roofContainers[action.payload];
      addOldStateToUndoChain(original(state)!, state, [container?.levelGuid]);

      state.trussEnvelopes = pickBy(
        state.trussEnvelopes,
        (trussEnvelope) => trussEnvelope.roofContainerGuid !== action.payload,
      );
      delete state.roofContainers[action.payload];
    },

    featurePatternContainerCreated(state, action: PayloadAction<FeaturePatternTrussContainer>) {
      addOldStateToUndoChain(original(state)!, state, [action.payload.levelGuid]);
      state.featurePatternContainers[action.payload.guid] = castDraft(action.payload);
    },

    featurePatternContainersUpserted(state, action: PayloadAction<readonly FeaturePatternTrussContainer[]>) {
      addOldStateToUndoChain(original(state)!, state, [
        ...action.payload.map((container) => state.featurePatternContainers[container.guid]?.levelGuid),
        ...action.payload.map((container) => container.levelGuid),
      ]);

      for (const featurePatternContainer of action.payload) {
        state.featurePatternContainers[featurePatternContainer.guid] = castDraft(featurePatternContainer);
      }
    },

    featureAreasCreated(
      state,
      action: PayloadAction<{ featurePatternContainerGuid: string; areas: LayoutFeatureArea[] }>,
    ) {
      addOldStateToUndoChain(original(state)!, state, [
        state.featurePatternContainers[action.payload.featurePatternContainerGuid]?.levelGuid,
      ]);

      const container = state.featurePatternContainers[action.payload.featurePatternContainerGuid];

      for (const area of action.payload.areas) {
        container.featureAreas.push(area);
      }
    },

    /**
     * The payload type of this action is somewhat convoluted, but this is required to allow for moving feature areas
     *   between containers, which is difficult to express because there is no container guid on feature areas.
     */
    featureAreasUpserted(state, action: PayloadAction<{ area: LayoutFeatureArea; containerGuid: string }[]>) {
      // a mapping of feature area guids to the feature pattern container guid that currently (pre-edit) contain them
      const preEditContainerGuids = new Map<string, string>();

      for (const { area, containerGuid } of action.payload) {
        // check the target container guid first
        const currentContainer = state.featurePatternContainers[containerGuid];
        if (currentContainer) {
          const indexInCurrentContainer = currentContainer.featureAreas.findIndex((a) => a.guid === area.guid);
          if (indexInCurrentContainer !== -1) {
            preEditContainerGuids.set(area.guid, containerGuid);
            continue;
          }
        }

        // otherwise we have no choice but to check every container
        for (const container of Object.values(state.featurePatternContainers)) {
          if (container.guid === containerGuid) continue; // already checked this one lol

          const indexInContainer = container.featureAreas.findIndex((a) => a.guid === area.guid);
          if (indexInContainer !== -1) {
            preEditContainerGuids.set(area.guid, container.guid);
            break;
          }
        }
      }

      addOldStateToUndoChain(
        original(state)!,
        state,
        [
          ...action.payload.map((payload) => payload.containerGuid),
          ...action.payload.map((payload) => preEditContainerGuids.get(payload.area.guid)),
        ]
          .filter(notNullish)
          .map((containerGuid) => state.featurePatternContainers[containerGuid]?.levelGuid),
      );

      for (const { area, containerGuid: targetContainerGuid } of action.payload) {
        const targetContainer = state.featurePatternContainers[targetContainerGuid];
        const currentContainer = state.featurePatternContainers[preEditContainerGuids.get(area.guid) ?? ''];

        // if the area is not in any container, it is newly created so we need to add it
        if (currentContainer == null) {
          targetContainer.featureAreas.push(area);
          continue;
        }

        const currentIndexInContainer = currentContainer.featureAreas.findIndex((a) => a.guid === area.guid);

        // if the area is already in the correct container, simply update it
        if (currentContainer === targetContainer) {
          currentContainer.featureAreas[currentIndexInContainer] = area;
          continue;
        }

        // else the area is in a different container and we need to move it
        targetContainer.featureAreas.push(area);
        currentContainer.featureAreas.splice(currentIndexInContainer, 1);
      }
    },

    featureAreasDeleted(state, action: PayloadAction<string[]>) {
      const featurePatternContainers = Object.values(state.featurePatternContainers);

      addOldStateToUndoChain(
        original(state)!,
        state,
        action.payload.map(
          (areaGuid) =>
            featurePatternContainers.find((c) => c.featureAreas.some((a) => a.guid === areaGuid))?.levelGuid,
        ),
      );

      for (const container of featurePatternContainers) {
        container.featureAreas = container.featureAreas.filter((area) => !action.payload.includes(area.guid));
      }
    },

    featurePatternContainersDeleted(state, action: PayloadAction<string[]>) {
      addOldStateToUndoChain(original(state)!, state, [
        ...action.payload.map((guid) => state.featurePatternContainers[guid]?.levelGuid),
      ]);

      for (const guid of action.payload) {
        delete state.featurePatternContainers[guid];

        const guidsOfContainedTrussEnvelopes = Object.entries(state.trussEnvelopes)
          .filter(([_guid, { featurePatternContainerGuid }]) => featurePatternContainerGuid === guid)
          .map(([guid]) => guid);

        for (const containedTrussEnvelopeGuid of guidsOfContainedTrussEnvelopes) {
          delete state.trussEnvelopes[containedTrussEnvelopeGuid];
        }
      }
    },

    clearTrussEnvelopes: (state) => {
      addOldStateToUndoChain(
        original(state)!,
        state,
        Object.values(state.trussEnvelopes).map((envelope) => envelope.levelGuid),
      );

      state.trussEnvelopes = {};
    },

    trussEnvelopeUpserted: (state, action: PayloadAction<TrussEnvelope>) => {
      addOldStateToUndoChain(original(state)!, state, [
        state.trussEnvelopes[action.payload.guid]?.levelGuid,
        action.payload.levelGuid,
      ]);

      state.trussEnvelopes[action.payload.guid] = castDraft(action.payload);
    },

    trussEnvelopesUpserted: (state, action: PayloadAction<readonly TrussEnvelope[]>) => {
      addOldStateToUndoChain(original(state)!, state, [
        ...action.payload.map((trussEnvelope) => state.trussEnvelopes[trussEnvelope.guid]?.levelGuid),
        ...action.payload.map((trussEnvelope) => trussEnvelope.levelGuid),
      ]);

      for (const trussEnvelope of action.payload) {
        state.trussEnvelopes[trussEnvelope.guid] = castDraft(trussEnvelope);
      }
    },

    trussEnvelopesDesigned: (
      state,
      action: PayloadAction<{
        readonly updatedTrussEnvelopes: readonly TrussEnvelope[];
        readonly errors: { readonly [trussEnvelopeGuid: string]: CreateTrussError };
      }>,
    ) => {
      addOldStateToUndoChain(
        original(state)!,
        state,
        [
          ...action.payload.updatedTrussEnvelopes.map((trussEnvelope) => trussEnvelope.guid),
          ...Object.keys(action.payload.errors),
        ].map((guid) => state.trussEnvelopes[guid]?.levelGuid),
      );

      Object.assign(state.trussEnvelopeErrors, action.payload.errors);

      for (const trussEnvelope of action.payload.updatedTrussEnvelopes) {
        delete state.trussEnvelopeErrors[trussEnvelope.guid];
        state.trussEnvelopes[trussEnvelope.guid] = castDraft(trussEnvelope);
      }
    },

    trussEnvelopesDeleted: (state, action: PayloadAction<string[]>) => {
      addOldStateToUndoChain(
        original(state)!,
        state,
        action.payload.map((guid) => state.trussEnvelopes[guid]?.levelGuid),
      );

      for (const guid of action.payload) {
        delete state.trussEnvelopes[guid];
      }
    },

    trussEnvelopeSnappingBehaviorSet: (state, action: PayloadAction<TrussEnvelopeSnappingBehaviorMode>) => {
      state.trussEnvelopeInsertionSettings.snapping.behaviorMode = action.payload;
    },

    trussEnvelopeSnappingSettingsReset: (state) => {
      state.trussEnvelopeInsertionSettings.snapping = castDraft(DEFAULT_TRUSS_ENVELOPE_SNAPPING_SETTINGS);
    },

    automaticTrussEnvelopeSnappingEnabled: (state) => {
      state.trussEnvelopeInsertionSettings.snapping.distanceSettings = 'automatic';
    },

    trussEnvelopeSnappingExtensionToggled: (state) => {
      if (state.trussEnvelopeInsertionSettings.snapping.distanceSettings === 'automatic') return;

      state.trussEnvelopeInsertionSettings.snapping.distanceSettings.extended =
        !state.trussEnvelopeInsertionSettings.snapping.distanceSettings.extended;
    },

    trussEnvelopeSnappingTargetLocked: (state, action: PayloadAction<SerializedEntitySnapTarget | null>) => {
      state.trussEnvelopeInsertionSettings.snapping.lockedTarget = action.payload;
    },

    trussEnvelopeSnappingDistancesSet: (state, action: PayloadAction<number[]>) => {
      if (state.trussEnvelopeInsertionSettings.snapping.distanceSettings === 'automatic') {
        state.trussEnvelopeInsertionSettings.snapping.distanceSettings = {
          distances: action.payload,
          extended: false,
        };
      } else {
        state.trussEnvelopeInsertionSettings.snapping.distanceSettings.distances = action.payload;
      }
    },

    pointSnappingTargetToggled: (
      state,
      action: PayloadAction<{ context: PointSnappingContext; target: EntitySnapTargetType }>,
    ) => {
      toggleEnabledEntitySnapTarget(state.pointSnappingSettings[action.payload.context], action.payload.target);
    },

    interactiveEditSnappingTargetToggled: (state, action: PayloadAction<EntitySnapTargetType>) => {
      toggleEnabledEntitySnapTarget(state.interactiveEditSnappingSettings, action.payload);
    },

    pointSnappingLengthIncrementToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].segmentSnaps.lengthIncrement =
        !state.pointSnappingSettings[action.payload.context].segmentSnaps.lengthIncrement;
    },

    pointSnappingIntersectionsToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].entitySnaps.intersections =
        !state.pointSnappingSettings[action.payload.context].entitySnaps.intersections;
    },

    pointSnappingExtensionIntersectionsToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].entitySnaps.extensionIntersections =
        !state.pointSnappingSettings[action.payload.context].entitySnaps.extensionIntersections;
    },

    pointSnappingMidpointsToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].entitySnaps.midpoints =
        !state.pointSnappingSettings[action.payload.context].entitySnaps.midpoints;
    },

    pointSnappingEndpointsToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].entitySnaps.endpoints =
        !state.pointSnappingSettings[action.payload.context].entitySnaps.endpoints;
    },

    pointSnappingNearestPointToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].entitySnaps.nearestPoint =
        !state.pointSnappingSettings[action.payload.context].entitySnaps.nearestPoint;
    },

    pointSnappingPerpendicularProjectionToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].entitySnaps.perpendicularProjection =
        !state.pointSnappingSettings[action.payload.context].entitySnaps.perpendicularProjection;
    },

    pointSnappingOrthogonalToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].segmentSnaps.orthogonal =
        !state.pointSnappingSettings[action.payload.context].segmentSnaps.orthogonal;
    },

    pointSnappingAngleToggled: (state, action: PayloadAction<{ context: PointSnappingContext }>) => {
      state.pointSnappingSettings[action.payload.context].segmentSnaps.angle =
        !state.pointSnappingSettings[action.payload.context].segmentSnaps.angle;
    },

    interactiveEditFootGridSnappingToggled: (state) => {
      state.interactiveEditSnappingSettings.footGrid = !state.interactiveEditSnappingSettings.footGrid;
    },

    interactiveEditEndpointAxisSnappingToggled: (state) => {
      state.interactiveEditSnappingSettings.entityAxisSnaps.endpoints =
        !state.interactiveEditSnappingSettings.entityAxisSnaps.endpoints;
    },

    interactiveEditMidpointAxisSnappingToggled: (state) => {
      state.interactiveEditSnappingSettings.entityAxisSnaps.midpoints =
        !state.interactiveEditSnappingSettings.entityAxisSnaps.midpoints;
    },

    interactiveEditAxisAlignedLineSnappingToggled: (state) => {
      state.interactiveEditSnappingSettings.entityAxisSnaps.axisAlignedLines =
        !state.interactiveEditSnappingSettings.entityAxisSnaps.axisAlignedLines;
    },

    pointSnappingSettingsReset: (state, action: PayloadAction<PointSnappingContext>) => {
      state.pointSnappingSettings[action.payload] = castDraft(initialState.pointSnappingSettings[action.payload]);
    },

    interactiveEditSnappingSettingsReset: (state) => {
      state.interactiveEditSnappingSettings = castDraft(initialState.interactiveEditSnappingSettings);
    },

    bearingEnvelopesUpserted: (
      state,
      action: PayloadAction<{
        readonly bearingEnvelopes: readonly BearingEnvelope[];
        readonly insertedBearingEnvelopeGuid?: string;
      }>,
    ) => {
      const { bearingEnvelopes, insertedBearingEnvelopeGuid } = action.payload;
      addOldStateToUndoChain(
        original(state)!,
        state,
        [
          ...bearingEnvelopes.map((bearingEnvelope) => state.bearingEnvelopes[bearingEnvelope.guid]?.levelGuid),
          ...bearingEnvelopes.map((bearingEnvelope) => bearingEnvelope.levelGuid),
        ],
        { insertedBearingEnvelopeGuid },
      );

      for (const bearingEnvelope of bearingEnvelopes) {
        state.bearingEnvelopes[bearingEnvelope.guid] = bearingEnvelope;
      }
    },

    bearingEnvelopesDeleted: (state, action: PayloadAction<string[]>) => {
      addOldStateToUndoChain(
        original(state)!,
        state,
        action.payload.map((guid) => state.bearingEnvelopes[guid]?.levelGuid),
      );

      for (const guid of action.payload) {
        delete state.bearingEnvelopes[guid];
      }
    },

    snappingUnderlaysUpserted: (state, action: PayloadAction<SnappingUnderlay[]>) => {
      addOldStateToUndoChain(original(state)!, state, NO_LEVELS_AFFECTED);

      for (const underlay of action.payload) {
        state.snappingUnderlays[underlay.guid] = castDraft(underlay);
      }
    },

    snappingUnderlaysDeleted: (state, action: PayloadAction<string[]>) => {
      addOldStateToUndoChain(original(state)!, state, NO_LEVELS_AFFECTED);

      for (const guid of action.payload) {
        delete state.snappingUnderlays[guid];
      }
    },

    workLinesUpserted(
      state,
      action: PayloadAction<{ workLines: readonly WorkLine[]; affectedLevelGuids: WorkLineAffectedLevelGuids }>,
    ) {
      addOldStateToUndoChain(original(state)!, state, action.payload.affectedLevelGuids ?? NO_LEVELS_AFFECTED);
      for (const workLine of action.payload.workLines) state.workLines[workLine.guid] = workLine;
    },

    workLinesBoundingToggled(
      state,
      action: PayloadAction<{ guids: readonly string[]; affectedLevelGuids: WorkLineAffectedLevelGuids }>,
    ) {
      addOldStateToUndoChain(original(state)!, state, action.payload.affectedLevelGuids ?? NO_LEVELS_AFFECTED);

      const { guids } = action.payload;
      const targetedLines = Object.values(state.workLines).filter((line) => guids.includes(line.guid));
      const allCurrentlyBounding = targetedLines.every((line) => line.bounding);

      for (const guid of guids) {
        state.workLines[guid].bounding = !allCurrentlyBounding;
      }
    },

    workLinesScopeChanged(
      state,
      action: PayloadAction<{
        guids: readonly string[];
        scope: WorkLineScope;
        affectedLevelGuids: WorkLineAffectedLevelGuids;
      }>,
    ) {
      addOldStateToUndoChain(original(state)!, state, action.payload.affectedLevelGuids ?? NO_LEVELS_AFFECTED);

      for (const guid of action.payload.guids) {
        state.workLines[guid].scope = action.payload.scope;
      }
    },

    workLinesDeleted(
      state,
      action: PayloadAction<{ guids: readonly string[]; affectedLevelGuids: WorkLineAffectedLevelGuids }>,
    ) {
      addOldStateToUndoChain(original(state)!, state, action.payload.affectedLevelGuids ?? NO_LEVELS_AFFECTED);

      for (const guid of action.payload.guids) {
        delete state.workLines[guid];
      }
    },

    userDistanceDimensionCreated(state, action: PayloadAction<LayoutUserDistanceDimension>) {
      addOldStateToUndoChain(original(state)!, state, [action.payload.levelGuid]);
      state.localUserDistanceDimensions.push(action.payload);
    },

    userDistanceDimensionDeleted(state) {
      const dimensionsArray =
        state.localUserDistanceDimensions.length > 0 ? state.localUserDistanceDimensions : state.userDistanceDimensions;
      addOldStateToUndoChain(original(state)!, state, [dimensionsArray[dimensionsArray.length - 1].levelGuid]);
      dimensionsArray.pop();
    },

    userDistanceDimensionsSaved(state) {
      addOldStateToUndoChain(
        original(state)!,
        state,
        state.localUserDistanceDimensions.map((d) => d.levelGuid),
      );
      state.userDistanceDimensions = [...state.userDistanceDimensions, ...state.localUserDistanceDimensions];
      state.localUserDistanceDimensions = [];
    },

    recentRoofPlaneInserted: (state, action: PayloadAction<string>) => {
      state.recentRoofPlaneInsertions = [...state.recentRoofPlaneInsertions, action.payload];
    },

    recentRoofPlanesCleared: (state) => {
      state.recentRoofPlaneInsertions = [];
    },

    roofPlaneCreated(
      state,
      action: PayloadAction<{
        readonly roofPlane: RoofPlane;

        // what to do after the insertion is complete
        readonly postInsertionAction: 'repeat' | 'finish' | 'nothing';

        // the settings that will be used if `postInsertionAction` is 'repeat'; included here because this action is
        //   handled by multiple reducers
        readonly roofPlaneInsertionSettings: LayoutState['roofPlaneInsertionSettings'];

        // When true, this creation is folded into the most recent undo entry rather than producing a new one.
        readonly extendsPreviousUndo?: boolean;
      }>,
    ) {
      const { roofPlane, extendsPreviousUndo } = action.payload;

      addOldStateToUndoChain(original(state)!, state, [roofPlane.levelGuid], { extendsPreviousUndo });

      state.roofPlanes[roofPlane.guid] = castDraft(roofPlane);
    },

    roofPlanesUpserted(state, action: PayloadAction<readonly RoofPlane[]>) {
      addOldStateToUndoChain(original(state)!, state, [
        ...action.payload.map((roofPlane) => state.roofPlanes[roofPlane.guid]?.levelGuid),
        ...action.payload.map((roofPlane) => roofPlane.levelGuid),
      ]);

      for (const roofPlane of action.payload) {
        state.roofPlanes[roofPlane.guid] = castDraft(roofPlane);
      }
    },

    roofPlanesDeleted(state, action: PayloadAction<string[]>) {
      addOldStateToUndoChain(
        original(state)!,
        state,
        action.payload.map((guid) => state.roofPlanes[guid]?.levelGuid),
      );

      for (const guid of action.payload) {
        delete state.roofPlanes[guid];
      }

      const deletedRoofPlaneGuidsSet = new Set(action.payload);

      // ensure that no remaining roof planes reference the deleted roof planes
      for (const remainingRoofPlane of Object.values(state.roofPlanes)) {
        remainingRoofPlane.cuts = remainingRoofPlane.cuts.filter(
          (cut) => cut.type !== 'AgainstPlane' || !deletedRoofPlaneGuidsSet.has(cut.cuttingPlaneGuid),
        );
      }

      // ensure that no truss envelope cuts reference the deleted roof planes
      for (const trussEnvelope of Object.values(state.trussEnvelopes)) {
        trussEnvelope.topCuts = trussEnvelope.topCuts.filter(
          (cut) => cut.type !== 'RoofPlane' || !deletedRoofPlaneGuidsSet.has(cut.roofPlaneGuid),
        );
        trussEnvelope.bottomCuts = trussEnvelope.bottomCuts.filter(
          (cut) => cut.type !== 'RoofPlane' || !deletedRoofPlaneGuidsSet.has(cut.roofPlaneGuid),
        );
        trussEnvelope.orderedTopCuts.cuts = trussEnvelope.orderedTopCuts.cuts.filter(
          (cut) => cut.type !== 'RoofPlane' || !deletedRoofPlaneGuidsSet.has(cut.roofPlaneGuid),
        );
        trussEnvelope.orderedBottomCuts.cuts = trussEnvelope.orderedBottomCuts.cuts.filter(
          (cut) => cut.type !== 'RoofPlane' || !deletedRoofPlaneGuidsSet.has(cut.roofPlaneGuid),
        );
      }
    },

    ceilingPlaneCreated(
      state,
      action: PayloadAction<{
        readonly ceilingPlane: CeilingPlane;

        // what to do after the insertion is complete
        readonly postInsertionAction: 'repeat' | 'finish';

        // the settings that will be used if `postInsertionAction` is 'repeat'; included here because this action is
        //   handled by multiple reducers
        readonly ceilingPlaneInsertionSettings: LayoutState['ceilingPlaneInsertionSettings'];
      }>,
    ) {
      const { ceilingPlane } = action.payload;

      addOldStateToUndoChain(original(state)!, state, [ceilingPlane.levelGuid]);

      state.ceilingPlanes[ceilingPlane.guid] = castDraft(ceilingPlane);
    },

    ceilingPlanesUpserted(state, action: PayloadAction<readonly CeilingPlane[]>) {
      addOldStateToUndoChain(original(state)!, state, [
        ...action.payload.map((ceilingPlane) => state.ceilingPlanes[ceilingPlane.guid]?.levelGuid),
        ...action.payload.map((ceilingPlane) => ceilingPlane.levelGuid),
      ]);

      for (const ceilingPlane of action.payload) {
        state.ceilingPlanes[ceilingPlane.guid] = castDraft(ceilingPlane);
      }
    },

    ceilingPlanesDeleted(state, action: PayloadAction<string[]>) {
      addOldStateToUndoChain(
        original(state)!,
        state,
        action.payload.map((guid) => state.ceilingPlanes[guid]?.levelGuid),
      );

      for (const guid of action.payload) {
        delete state.ceilingPlanes[guid];
      }

      const deletedCeilingPlaneGuidsSet = new Set(action.payload);

      // ensure that no remaining ceiling planes reference the deleted ceiling planes
      for (const remainingCeilingPlane of Object.values(state.ceilingPlanes)) {
        remainingCeilingPlane.cuts = remainingCeilingPlane.cuts.filter(
          (cut) => cut.type !== 'AgainstPlane' || !deletedCeilingPlaneGuidsSet.has(cut.cuttingPlaneGuid),
        );
      }

      // ensure that no truss envelope cuts reference the deleted ceiling planes
      for (const trussEnvelope of Object.values(state.trussEnvelopes)) {
        trussEnvelope.topCuts = trussEnvelope.topCuts.filter(
          (cut) => cut.type !== 'CeilingPlane' || !deletedCeilingPlaneGuidsSet.has(cut.ceilingPlaneGuid),
        );
        trussEnvelope.bottomCuts = trussEnvelope.bottomCuts.filter(
          (cut) => cut.type !== 'CeilingPlane' || !deletedCeilingPlaneGuidsSet.has(cut.ceilingPlaneGuid),
        );
        trussEnvelope.orderedTopCuts.cuts = trussEnvelope.orderedTopCuts.cuts.filter(
          (cut) => cut.type !== 'CeilingPlane' || !deletedCeilingPlaneGuidsSet.has(cut.ceilingPlaneGuid),
        );
        trussEnvelope.orderedBottomCuts.cuts = trussEnvelope.orderedBottomCuts.cuts.filter(
          (cut) => cut.type !== 'CeilingPlane' || !deletedCeilingPlaneGuidsSet.has(cut.ceilingPlaneGuid),
        );
      }
    },

    cutFacetHovered(
      state,
      action: PayloadAction<{
        targetInfo: NonInsertionTrussEnvelopeTargetInfo;
        topOrBottom: 'top' | 'bottom';
        cutIndex: number;
      } | null>,
    ) {
      state.hoveredCutFacet = action.payload;
    },

    highlightedCeilingPlaneSet(state, action: PayloadAction<string | null>) {
      if (action.payload) {
        state.highlightedPlane = { type: 'ceilingPlane', guid: action.payload };
      } else if (state.highlightedPlane?.type === 'ceilingPlane') {
        state.highlightedPlane = null;
      }
    },

    highlightedRoofPlaneSet(state, action: PayloadAction<string | null>) {
      if (action.payload) {
        state.highlightedPlane = { type: 'roofPlane', guid: action.payload };
      } else if (state.highlightedPlane?.type === 'roofPlane') {
        state.highlightedPlane = null;
      }
    },

    highlightedAbsoluteVerticalPlaneSet(
      state,
      action: PayloadAction<Pick<AbsoluteVerticalCut, 'segment' | 'isInfinite'>>,
    ) {
      if (action.payload) {
        state.highlightedPlane = { ...action.payload, type: 'absoluteVertical' };
      } else if (state.highlightedPlane?.type === 'absoluteVertical') {
        state.highlightedPlane = null;
      }
    },

    highlightedPlanesCleared(state) {
      state.highlightedPlane = null;
    },

    hoveredPlaneSet(state, action: PayloadAction<string>) {
      state.hoveredRoofFace = { type: 'roofOrCeilingPlane', guid: action.payload };
    },

    hoveredCeilingTraySet(state, action: PayloadAction<string>) {
      state.hoveredRoofFace = { type: 'ceilingTrayPolygon', guid: action.payload };
    },

    hoveredRoofFaceCleared(state) {
      state.hoveredRoofFace = null;
    },

    ceilingTrayCreated(state, action: PayloadAction<CeilingTray>) {
      addOldStateToUndoChain(original(state)!, state, [action.payload.levelGuid]);
      state.ceilingTrays[action.payload.guid] = action.payload;
    },

    ceilingTraysUpserted(state, action: PayloadAction<CeilingTray[]>) {
      addOldStateToUndoChain(original(state)!, state, [
        ...action.payload.map((ceilingTray) => state.ceilingTrays[ceilingTray.guid]?.levelGuid),
        ...action.payload.map((ceilingTray) => ceilingTray.levelGuid),
      ]);

      for (const ceilingTray of action.payload) {
        state.ceilingTrays[ceilingTray.guid] = ceilingTray;
      }
    },

    ceilingTraysDeleted(state, action: PayloadAction<string[]>) {
      addOldStateToUndoChain(
        original(state)!,
        state,
        action.payload.map((guid) => state.ceilingTrays[guid]?.levelGuid),
      );

      for (const guid of action.payload) {
        delete state.ceilingTrays[guid];
      }
    },

    insertionBearingEnvelopeElevationsSynchronizedWithLevel(
      state,
      action: PayloadAction<{ bearingType: BearingEnvelopeType }>,
    ) {
      getBearingEnvelopeInsertionSettings(state, action.payload.bearingType).setElevationsFromLevel = true;
    },

    bearingEnvelopeInsertionRoofPlaneInsertionModeSet(
      state,
      action: PayloadAction<{ bearingType: BearingEnvelopeType; insertRoofPlane: boolean }>,
    ) {
      getBearingEnvelopeInsertionSettings(state, action.payload.bearingType).insertRoofPlane =
        action.payload.insertRoofPlane;
    },

    bearingEnvelopeInsertionAutoInsertOnSecondPointSet(
      state,
      action: PayloadAction<{ bearingType: BearingEnvelopeType; autoInsertOnSecondPoint: boolean }>,
    ) {
      getBearingEnvelopeInsertionSettings(state, action.payload.bearingType).autoInsertOnSecondPoint =
        action.payload.autoInsertOnSecondPoint;
    },

    roofPlaneInsertionAutoCutSet(state, action: PayloadAction<boolean>) {
      state.roofPlaneInsertionSettings.autoCutOnInsert = action.payload;
    },

    bearingEnvelopeInsertionLockedLengthSet(
      state,
      action: PayloadAction<{ bearingType: BearingEnvelopeType; lockedLength: number | null }>,
    ) {
      getBearingEnvelopeInsertionSettings(state, action.payload.bearingType).lockedLength = action.payload.lockedLength;
    },

    bearingEnvelopeInsertionLockedDirectionSet(
      state,
      action: PayloadAction<{ bearingType: BearingEnvelopeType; lockedDirection: CardinalDirection | null }>,
    ) {
      getBearingEnvelopeInsertionSettings(state, action.payload.bearingType).lockedDirection =
        action.payload.lockedDirection;
    },

    hoveredWorkLinesSet(state, action: PayloadAction<readonly string[]>) {
      state.hoveredWorkLineGuids = castDraft(action.payload);
    },

    hoveredBearingEnvelopeEdgeSet(state, action: PayloadAction<{ guid: string; edgeAngle: number }>) {
      state.hoveredBearingEnvelopeEdge = action.payload;
    },

    hoveredBearingEnvelopeEdgeCleared(state) {
      state.hoveredBearingEnvelopeEdge = null;
    },

    hoveredBearingEnvelopeInteriorSet(state, action: PayloadAction<{ guid: string }>) {
      state.hoveredBearingEnvelopeInterior = action.payload;
    },

    hoveredBearingEnvelopeInteriorCleared(state) {
      state.hoveredBearingEnvelopeInterior = null;
    },

    universalLevelSet(state, action: PayloadAction<string>) {
      for (const roofContainer of Object.values(state.roofContainers)) {
        roofContainer.levelGuid = action.payload;
      }

      for (const featurePatternContainer of Object.values(state.featurePatternContainers)) {
        featurePatternContainer.levelGuid = action.payload;
      }

      for (const trussEnvelope of Object.values(state.trussEnvelopes)) {
        trussEnvelope.levelGuid = action.payload;
      }

      for (const roofPlane of Object.values(state.roofPlanes)) {
        roofPlane.levelGuid = action.payload;
      }

      for (const ceilingPlane of Object.values(state.ceilingPlanes)) {
        ceilingPlane.levelGuid = action.payload;
      }

      for (const ceilingTray of Object.values(state.ceilingTrays)) {
        ceilingTray.levelGuid = action.payload;
      }

      for (const bearingEnvelope of Object.values(state.bearingEnvelopes)) {
        bearingEnvelope.levelGuid = action.payload;
      }

      // this is not an undoable action
      state.undoRedoChain = castDraft(BASE_UNDO_REDO_CHAIN);
    },

    undo: (
      state,
      action: PayloadAction<{
        readonly validComponentDesignGuids: readonly string[];
        readonly restoresBearingEnvelopeInsertionChain?: boolean;
      }>,
    ) => {
      if (!state.undoRedoChain.undo) return;

      // Make sure we're only including the part of layout state that can be undone/redone.
      const strippedCurrentState = pick(state, undoRedoKeys);

      const stateToUndoTo = state.undoRedoChain.undo.state;
      const cleanedTrussEnvelopes = cleanTrussEnvelopeComponentDesignReferences(
        stateToUndoTo.trussEnvelopes,
        action.payload.validComponentDesignGuids,
      );

      return {
        ...state,
        ...stateToUndoTo,
        trussEnvelopes: cleanedTrussEnvelopes,
        undoRedoChain: {
          undo: state.undoRedoChain.undo.previous,
          redo: {
            state: castDraft(strippedCurrentState),
            next: state.undoRedoChain.redo,
            affectedLevels: state.undoRedoChain.affectedLevelsForCurrentChanges,
            insertedBearingEnvelopeGuid: state.undoRedoChain.undo.insertedBearingEnvelopeGuid,
            restoresBearingEnvelopeInsertionChain: action.payload.restoresBearingEnvelopeInsertionChain,
          },

          affectedLevelsForCurrentChanges: state.undoRedoChain.undo.affectedLevels,
        },
      };
    },

    redo: (state, action: PayloadAction<{ readonly validComponentDesignGuids: readonly string[] }>) => {
      if (!state.undoRedoChain.redo) return;

      // Make sure we're only including the part of layout state that can be undone/redone.
      const strippedCurrentState = pick(state, undoRedoKeys);

      const stateToRedoTo = state.undoRedoChain.redo.state;
      const cleanedTrussEnvelopes = cleanTrussEnvelopeComponentDesignReferences(
        stateToRedoTo.trussEnvelopes,
        action.payload.validComponentDesignGuids,
      );

      return {
        ...state,
        ...stateToRedoTo,
        trussEnvelopes: cleanedTrussEnvelopes,
        undoRedoChain: {
          undo: {
            state: castDraft(strippedCurrentState),
            previous: state.undoRedoChain.undo,
            affectedLevels: state.undoRedoChain.affectedLevelsForCurrentChanges,
            insertedBearingEnvelopeGuid: state.undoRedoChain.redo.insertedBearingEnvelopeGuid,
          },
          redo: state.undoRedoChain.redo.next,

          affectedLevelsForCurrentChanges: state.undoRedoChain.redo.affectedLevels,
        },
      };
    },

    /**
     * Makes every change since the undo history's `since` link undo as one step. Leaves the history alone if `since` is
     *   no longer in it, such as after undoing past it.
     */
    undoStepsMerged: (state, action: PayloadAction<{ readonly since: UndoSnapshot | null }>) => {
      const chain = original(state)!.undoRedoChain;
      const newLinks: UndoSnapshot[] = [];
      for (let link = chain.undo; link !== action.payload.since; link = link.previous) {
        if (link == null) return;
        newLinks.push(link);
      }
      if (newLinks.length < 2) return;

      // The oldest new link holds the layout from before the changes; the rest only add to the levels they touched.
      const oldest = newLinks[newLinks.length - 1];
      state.undoRedoChain = castDraft({
        undo: { state: oldest.state, previous: oldest.previous, affectedLevels: oldest.affectedLevels },
        redo: chain.redo,
        affectedLevelsForCurrentChanges: uniq([
          ...chain.affectedLevelsForCurrentChanges,
          ...newLinks.slice(0, -1).flatMap((link) => link.affectedLevels),
        ]),
      });
    },
  },

  extraReducers(builder) {
    builder.addCase(applicationOnInsertingBearingEnvelope, (state, action) => {
      const { bearingType } = action.payload.insertionData;
      const settings = getBearingEnvelopeInsertionSettings(state, bearingType);

      settings.defaultProperties =
        bearingType === 'Beam'
          ? pick(action.payload.insertionData, BEAM_PROPERTIES_PERSISTED_IN_SETTINGS)
          : pick(action.payload.insertionData, WALL_PROPERTIES_PERSISTED_IN_SETTINGS);
      settings.lockedLength = null;
      settings.lockedDirection = null;
    });

    builder.addCase(updateInsertingBearingEnvelope, (state, action) => {
      const { bearingType } = action.payload;
      const settings = getBearingEnvelopeInsertionSettings(state, bearingType);
      const persistedKeys =
        bearingType === 'Beam' ? BEAM_PROPERTIES_PERSISTED_IN_SETTINGS : WALL_PROPERTIES_PERSISTED_IN_SETTINGS;

      if (settings.defaultProperties) {
        Object.assign(settings.defaultProperties, pick(action.payload.changes, persistedKeys));
      }

      // if elevations are manually edited, we're no longer locked / synced to the active level

      const elevationsWereFromLevel = settings.setElevationsFromLevel === true;
      const actionModifiesElevation = action.payload.changes.bottom != null || action.payload.changes.top != null;
      const actionChangesElevationRules = action.payload.changes.beamOrientation != null;

      if (elevationsWereFromLevel && (actionModifiesElevation || actionChangesElevationRules)) {
        settings.setElevationsFromLevel = false;
      }

      // Once the right point is set, the bearing's length is determined by its endpoints, so any locked length is no
      //   longer meaningful and would override the displayed length on subsequent edits.
      if (action.payload.changes.rightPoint != null) {
        settings.lockedLength = null;
      }
    });

    builder.addCase(applicationOnPickingLayoutPoint, (state, action) => {
      if (action.payload.target === 'bearing-envelope-left' || action.payload.target === 'bearing-envelope-right') {
        state.bearingEnvelopeInsertionSettings.beam.lockedLength = null;
        state.bearingEnvelopeInsertionSettings.beam.lockedDirection = null;
        state.bearingEnvelopeInsertionSettings.wall.lockedLength = null;
        state.bearingEnvelopeInsertionSettings.wall.lockedDirection = null;
      }
    });

    builder.addCase(updateInsertingRoofPlane, (state, action) => {
      Object.assign(
        state.roofPlaneInsertionSettings.defaultProperties,
        pick(action.payload, ROOF_PLANE_PROPERTIES_PERSISTED_IN_SETTINGS),
      );

      const elevation = (() => {
        const { geometry } = action.payload;

        switch (geometry?.type) {
          case 'Absolute':
            return geometry.elevation;

          case 'BearingEnvelope':
            return state.bearingEnvelopes[geometry.bearingEnvelopeGuid]?.top ?? null;

          default:
            return null;
        }
      })();

      if (elevation != null) {
        state.roofPlaneInsertionSettings.defaultElevationIfAbsolute = elevation;
      }
    });

    builder.addCase(updateInsertingCeilingPlane, (state, action) => {
      Object.assign(
        state.ceilingPlaneInsertionSettings.defaultProperties,
        pick(action.payload, CEILING_PLANE_PROPERTIES_PERSISTED_IN_SETTINGS),
      );

      const elevation = (() => {
        const { geometry } = action.payload;

        switch (geometry?.type) {
          case 'Absolute':
            return geometry.elevation;

          case 'BearingEnvelope':
            return state.bearingEnvelopes[geometry.bearingEnvelopeGuid]?.top ?? null;

          default:
            return null;
        }
      })();

      if (elevation != null) {
        state.ceilingPlaneInsertionSettings.defaultElevationIfAbsolute = elevation;
      }
    });

    builder.addCase(updateInsertionFeaturePatternContainer, (state, action) => {
      if ('slopeSettings' in action.payload) {
        state.featurePatternContainerInsertionSettings.defaultSlopeSettings = action.payload.slopeSettings ?? null;
      }

      const { shape } = action.payload;
      if (shape && shape.topElevation != null && shape.bottomElevation != null) {
        state.featurePatternContainerInsertionSettings.defaultElevations = {
          topElevation: shape.topElevation,
          bottomElevation: shape.bottomElevation,
        };
      }
    });

    builder.addCase(applicationOnInsertingTrussEnvelope, (state, action) => {
      // We want target locking to be transient but there is no single action dispatched after an insertion is complete.
      //   Clearing it here takes care of that when normal insertion is happening.
      state.trussEnvelopeInsertionSettings.snapping.lockedTarget = null;

      const settings = state.trussEnvelopeInsertionSettings;
      settings.lastComponentType = action.payload.componentType;
      if (action.payload.componentType === 'RoofTruss') {
        settings.lastRoofTrussDefinitionType = action.payload.definitionType;
      }
    });

    builder.addCase(resetInsertionTrussEnvelope, (state) => {
      // We want target locking to be transient but there is no single action dispatched after an insertion is complete.
      //   Clearing it here takes care of that when "insert and repeat" is used.
      state.trussEnvelopeInsertionSettings.snapping.lockedTarget = null;
    });

    builder.addCase(setInsertionTrussEnvelopeProperties, (state, action) => {
      const settings = state.trussEnvelopeInsertionSettings;

      if (action.payload.componentType != null) settings.lastComponentType = action.payload.componentType;

      if (action.payload.definitionType != null && settings.lastComponentType === 'RoofTruss') {
        settings.lastRoofTrussDefinitionType = action.payload.definitionType;
      }

      Object.assign(
        settings.defaultProperties[
          getDefinitionTypeForComponentType(settings.lastComponentType, settings.lastRoofTrussDefinitionType)
        ],
        pick(action.payload, TRUSS_ENVELOPE_PROPERTIES_PERSISTED_IN_SETTINGS),
      );
    });

    builder.addCase(applyUserSettings, (state, action) => {
      state.trussEnvelopeInsertionSettings.bevelCutTypePreference =
        action.payload.layoutSettings.bevelCutTypePreference;
    });

    builder.addCase(setDimensionMode, (state, action) => {
      const dimensionModeOn = action.payload;
      if (!dimensionModeOn) state.localUserDistanceDimensions = [];
    });

    builder.addMatcher(
      isAnyOf(
        sceneHoverCleared,
        interactiveEditingSlice.actions.__UNSAFE_startLengthEdit,
        interactiveEditingSlice.actions.__UNSAFE_startMoveEdit,
      ),
      (state: Draft<LayoutState>) => {
        state.hoveredRoofFace = null;
        if (state.hoveredWorkLineGuids.length > 0) state.hoveredWorkLineGuids = [];
        state.hoveredBearingEnvelopeEdge = null;
        state.hoveredBearingEnvelopeInterior = null;
      },
    );
  },
});

export const layout = layoutSlice.reducer;

// Since we create new objects for layout undo/redo state, object equality is not sufficient for finding equal states.
// Instead, we need to compare each property.
function areLayoutUndoRedoStatesEqual(state1: LayoutUndoRedoState, state2: LayoutUndoRedoState) {
  for (const key of undoRedoKeys) {
    if (state1[key] !== state2[key]) return false;
  }

  return true;
}

/**
 * `addOldStateToUndoChain` accepts `undefined` level guids to make constructing the affected levels argument easier.
 *   These values will be filtered out and have no effect. Note that this behavior does not apply to `null` values,
 *   which are used to explicitly communicate that an object is not associated with a level.
 *
 * IMPORTANT NOTE: do not pass an empty array literal to this function. This is a rare situation (currently only
 *   expected when working with work lines) and will not allow us to distinguish between actions that didn't affect any
 *   levels and actions where the affected level detection failed. To explicitly indicate that an undo action did not
 *   affect any levels, pass in `NO_LEVELS_AFFECTED` instead.
 */
type LooseAffectedLevelGuids = readonly (string | undefined | null)[];

const NO_LEVELS_AFFECTED = Object.freeze([]);

interface AddOldStateToUndoChainOptions {
  // When true, this action is folded into the most recent undo entry rather than producing a new one.
  readonly extendsPreviousUndo?: boolean;

  // Required when the action being added to the chain represents a bearing envelope insertion. Used to restore the
  //   in-progress insertion state.
  readonly insertedBearingEnvelopeGuid?: string;
}

function addOldStateToUndoChain(
  oldState: LayoutState,
  currentDraftState: Draft<LayoutState>,
  looseAffectedLevels: LooseAffectedLevelGuids,
  options?: AddOldStateToUndoChainOptions,
) {
  const actualAffectedLevels = uniq(looseAffectedLevels.filter(notUndefined));
  assertUnlessInProduction(
    actualAffectedLevels.length > 0 || looseAffectedLevels === NO_LEVELS_AFFECTED,
    'We failed to track the affected levels for a layout edit.',
  );

  const existingUndoChainLink = currentDraftState.undoRedoChain.undo;

  if (options?.extendsPreviousUndo && existingUndoChainLink) {
    currentDraftState.undoRedoChain.affectedLevelsForCurrentChanges = uniq([
      ...currentDraftState.undoRedoChain.affectedLevelsForCurrentChanges,
      ...actualAffectedLevels,
    ]);
    return;
  }

  // if no changes have actually been made since the last undo entry, simply update the affected levels
  if (existingUndoChainLink && areLayoutUndoRedoStatesEqual(existingUndoChainLink.state, oldState)) {
    currentDraftState.undoRedoChain.affectedLevelsForCurrentChanges = actualAffectedLevels;
    return;
  }

  // make sure we're only including the part of layout state that can be undone/redone.
  const strippedUndoState = pick(oldState, undoRedoKeys);

  currentDraftState.undoRedoChain = {
    undo: {
      state: castDraft(strippedUndoState),
      previous: currentDraftState.undoRedoChain.undo,
      affectedLevels: currentDraftState.undoRedoChain.affectedLevelsForCurrentChanges,
      insertedBearingEnvelopeGuid: options?.insertedBearingEnvelopeGuid,
    },

    redo: null,

    affectedLevelsForCurrentChanges: actualAffectedLevels,
  };
}

/**
 * We can't ensure that component design GUIDs that exist in undo/redo state are still valid. This method ensure that
 * we're referencing valid component designs.
 */
function cleanTrussEnvelopeComponentDesignReferences(
  trussEnvelopes: ReadonlyRecord<string, TrussEnvelope>,
  validComponentDesignGuids: readonly string[],
) {
  return mapValues(trussEnvelopes, (trussEnvelope) =>
    trussEnvelope.componentDesignGuid == null || validComponentDesignGuids.includes(trussEnvelope.componentDesignGuid)
      ? trussEnvelope
      : { ...trussEnvelope, componentDesignGuid: null },
  );
}
